// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { DEFAULT_VALUES, useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";

const flexWrap = css`
    display: flex;

    ${UNSAFE_media.below.sm} {
        flex-wrap: wrap;
    }
`;

const blogLinkStyles = css`
    margin: ${token("space.200")} ${token("space.200")} ${token("space.400")};
    font-size: ${token("space.200")};

    img {
        margin-bottom: ${token("space.100")};
    }
`;

const blogLinkTextStyles = css`
    &:link,
    &:visited {
        color: #0052cc;
        text-decoration: none;
    }

    &:hover {
        color: #0065ff;

        .link-arrow {
            transition: margin-left 0.25s ease-in-out;
            margin-left: ${token("space.050")};
        }
        .link-text {
            text-decoration: underline;
        }
    }

    &:hover::after {
        .link-arrow {
            transition: margin-left 0.25s ease-in-out;
            margin-left: ${token("space.0")};
        }
    }
`;

const BlogsSection = () => {
    const intl = useIntl();

    return (
        <section
            css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: ${token("space.600")} ${token("space.200")} 36px;
                background-color: #ffffff;
            `}
        >
            <div
                css={css`
                    display: flex;
                    max-width: 1440px;

                    ${UNSAFE_media.below.md} {
                        display: block;
                    }
                `}
            >
                <div
                    css={css`
                        ${UNSAFE_media.below.md} {
                            display: grid;
                            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
                        }

                        ${UNSAFE_media.below.sm} {
                            grid-template-columns: minmax(0, 1fr);
                        }
                    `}
                >
                    <div
                        css={css`
                            width: 58.3%;
                            padding: ${token("space.200")};

                            ${UNSAFE_media.below.md} {
                                width: 90%;
                                padding-right: 0;
                            }

                            ${media.above.md} {
                                transform: translate3d(-20%, 0, 0);
                            }
                        `}
                    >
                        <Image
                            src="https://wac-cdn.atlassian.com/dam/jcr:aa8f5b1f-b8ac-4d6e-9e7c-abe573b68164/number-one.svg?cdnVersion=1198"
                            alt="#1"
                        />
                    </div>
                    <div
                        css={css`
                            margin-left: 16.6%;
                            padding: ${token("space.200")};

                            ${media.above.md} {
                                width: 66%;
                            }

                            ${UNSAFE_media.below.md} {
                                margin-left: 0;
                            }

                            h2 {
                                font-size: 2.3rem;
                                color: #344563;

                                ${UNSAFE_media.below.sm} {
                                    font-size: 1.5rem;
                                }
                            }

                            p {
                                font-size: 1rem;
                                font-weight: normal;
                                color: #344563;
                            }
                        `}
                    >
                        <ContentBlock
                            headerType="h2"
                            headerText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.headerText",
                                defaultMessage:
                                    "Why Jira is the #1 tool recommended by agile teams.*"
                            })}
                            bodyText={
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.blogsSection.bodyText"
                                    defaultMessage="Trusted by more than 100,000 organizations, Jira Software comes out-of-the-box with the features and best practices agile teams need to develop and evolve their agile practices."
                                    values={DEFAULT_VALUES}
                                />
                            }
                        />
                        <div
                            css={css`
                                p {
                                    font-size: 0.9rem;
                                    color: #42526e;
                                    margin: 0;
                                }
                            `}
                        >
                            <p>
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.blogsSection.contentFooter"
                                    defaultMessage="*2021 State of Agile Report"
                                    values={DEFAULT_VALUES}
                                />
                            </p>
                        </div>
                    </div>
                </div>
                <div>
                    <div css={flexWrap}>
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/dam/jcr:c30fba51-a9ab-4815-ad95-83e3f7582349/resource-agile.png?cdnVersion=1198"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.agileAltText",
                                defaultMessage: "Agile illustration"
                            })}
                            href={useLocalizedHref("/agile")}
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.agileLinkText",
                                defaultMessage: "Learn agile best practices"
                            })}
                        />
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/resource-playbook.webp"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.teamPlaybookAltText",
                                defaultMessage: "Team playbook illustration"
                            })}
                            href={useLocalizedHref("/team-playbook")}
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.teamPlaybookLinkText",
                                defaultMessage: "Team Playbook"
                            })}
                        />
                    </div>
                    <div css={flexWrap}>
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/resource-community.webp"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.communityAltText",
                                defaultMessage: "Community illustration"
                            })}
                            href="https://community.atlassian.com/"
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.communityLinkText",
                                defaultMessage: "Jira Software community"
                            })}
                        />
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/dam/jcr:1de028c2-f441-40e9-b3ee-721e35e4a649/resource-devops.png?cdnVersion=1198"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.devOpsAltText",
                                defaultMessage: "DevOps loop illustration"
                            })}
                            href={useLocalizedHref("/devops")}
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.devOpsLinkText",
                                defaultMessage: "Learn DevOps best practices"
                            })}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

const BlogLink = (props: {
    imageSrc: string;
    imageAlt: string;
    href: string;
    linkText: string;
}) => {
    return (
        <div css={blogLinkStyles}>
            <a
                href={props.href}
                data-event-component="linkButton"
                data-event-container="blogsSection"
                data-event="clicked"
                data-label={props.linkText}
                data-product-key="jiraSoftware"
            >
                <Image
                    src={props.imageSrc}
                    alt={props.imageAlt}
                    aspectRatio="16/9"
                />
            </a>
            {blogLinkText(props.href, props.linkText)}
        </div>
    );
};

const blogLinkText = (href: string, linkText: string) => {
    return (
        <div>
            <a
                href={href}
                css={blogLinkTextStyles}
                data-event-component="linkButton"
                data-event-container="blogsSection"
                data-event="clicked"
                data-label={linkText}
                data-product-key="jiraSoftware"
            >
                <div
                    className="link-text"
                    css={css`
                        display: inline-flex;
                        font-size: ${token("space.200")};
                    `}
                >
                    {linkText}
                    <div className="link-arrow">{linkArrow()}</div>
                </div>
            </a>
        </div>
    );
};

const linkArrow = () => {
    return (
        <svg
            css={css`
                margin-left: ${token("space.075")};
            `}
            fill="currentColor"
            width="11"
            height="8"
            viewBox="0 0 11 8"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.40796 0L6.32831 1.2021L8.07854 3.1497H0.763182C0.341906 3.1497 0 3.53038 0 3.99943C0 4.46849 0.341906 4.84917 0.763182 4.84917H8.07854L6.32831 6.7979L7.40796 8L11 3.99943L7.40796 0Z"
            />
        </svg>
    );
};

export default BlogsSection;
